import React from "react";

/*
 Smart technical elevation drawing.
 Renders a scaled schematic for a given typology + width/height (mm),
 mimicking architectural window/door elevations with opening symbols.
*/

const FRAME = "#334155";
const MULLION = "#64748b";
const GLASS = "#DBEAFE";
const GLASS2 = "#EFF6FF";
const SWING = "#2563eb";
const BLADE = "#94a3b8";

function normalize(t = "") {
  return t.toLowerCase();
}

export function WindowDiagram({
  typology = "Fixed",
  width = 1000,
  height = 1000,
  code = "",
  elevation = "",
  className = "",
  showDims = true,
}) {
  const w = Math.max(Number(width) || 0, 1);
  const h = Math.max(Number(height) || 0, 1);
  const MAX = 190;
  const scale = MAX / Math.max(w, h);
  const frameW = Math.max(w * scale, 34);
  const frameH = Math.max(h * scale, 34);

  const padTop = showDims ? 26 : 8;
  const padRight = showDims ? 46 : 8;
  const padLeft = 12;
  const padBottom = 12;

  const x0 = padLeft;
  const y0 = padTop;
  const x1 = x0 + frameW;
  const y1 = y0 + frameH;
  const svgW = padLeft + frameW + padRight;
  const svgH = padTop + frameH + padBottom;

  const inset = 5;
  const gx = x0 + inset;
  const gy = y0 + inset;
  const gw = frameW - inset * 2;
  const gh = frameH - inset * 2;

  const type = normalize(typology);
  const els = [];

  // ---- glass panel helper ----
  const panel = (px, py, pw, ph, key, fill = GLASS) => (
    <rect
      key={key}
      x={px}
      y={py}
      width={pw}
      height={ph}
      fill={fill}
      stroke={MULLION}
      strokeWidth={1.2}
    />
  );

  // ---- slide arrow ----
  const slideArrow = (cx, cy, len, dir, key) => {
    const half = len / 2;
    const a = cx - half;
    const b = cx + half;
    return (
      <g key={key} stroke={SWING} strokeWidth={1.4} fill="none">
        <line x1={a} y1={cy} x2={b} y2={cy} />
        {dir !== "right" && <polyline points={`${a + 5},${cy - 3} ${a},${cy} ${a + 5},${cy + 3}`} />}
        {dir !== "left" && <polyline points={`${b - 5},${cy - 3} ${b},${cy} ${b - 5},${cy + 3}`} />}
      </g>
    );
  };

  // ---- casement swing symbol (dashed triangle, apex at hinge) ----
  const swingSymbol = (px, py, pw, ph, hinge, key) => {
    let pts;
    if (hinge === "left") pts = `${px},${py + ph / 2} ${px + pw},${py} ${px + pw},${py + ph}`;
    else if (hinge === "right") pts = `${px + pw},${py + ph / 2} ${px},${py} ${px},${py + ph}`;
    else if (hinge === "top") pts = `${px + pw / 2},${py} ${px},${py + ph} ${px + pw},${py + ph}`;
    else pts = `${px + pw / 2},${py + ph} ${px},${py} ${px + pw},${py}`;
    return (
      <polygon
        key={key}
        points={pts}
        fill="none"
        stroke={SWING}
        strokeWidth={1}
        strokeDasharray="3 2"
      />
    );
  };

  // ---- build interior by typology ----
  if (type.includes("ventilator") || type.includes("louver")) {
    els.push(panel(gx, gy, gw, gh, "vg", GLASS2));
    const blades = Math.max(3, Math.round(gh / 12));
    const step = gh / blades;
    for (let i = 0; i < blades; i++) {
      const yy = gy + step * (i + 0.5);
      els.push(
        <line
          key={`bl${i}`}
          x1={gx + 2}
          y1={yy - 2}
          x2={gx + gw - 2}
          y2={yy + 2}
          stroke={BLADE}
          strokeWidth={2}
        />
      );
    }
  } else if (type.includes("tilt")) {
    // Tilt & Turn — side turn + bottom tilt symbols
    els.push(panel(gx, gy, gw, gh, "tt"));
    els.push(swingSymbol(gx, gy, gw, gh, "left", "tt-turn"));
    els.push(swingSymbol(gx, gy, gw, gh, "bottom", "tt-tilt"));
    els.push(<circle key="tt-h" cx={gx + gw - 5} cy={gy + gh / 2} r={2.2} fill={FRAME} />);
  } else if (type.includes("bottom hung") || type.includes("hopper")) {
    els.push(panel(gx, gy, gw, gh, "bh"));
    els.push(swingSymbol(gx, gy, gw, gh, "bottom", "bh-s"));
  } else if (type.includes("french")) {
    const half = gw / 2;
    els.push(panel(gx, gy, half, gh, "fd-l"));
    els.push(panel(gx + half, gy, gw - half, gh, "fd-r"));
    els.push(swingSymbol(gx, gy, half, gh, "left", "fd-sl"));
    els.push(swingSymbol(gx + half, gy, gw - half, gh, "right", "fd-sr"));
    els.push(<line key="fd-m" x1={gx + half} y1={gy} x2={gx + half} y2={gy + gh} stroke={MULLION} strokeWidth={1} strokeDasharray="2 2" />);
    els.push(<circle key="fd-hl" cx={gx + half - 5} cy={gy + gh / 2} r={2} fill={FRAME} />);
    els.push(<circle key="fd-hr" cx={gx + half + 5} cy={gy + gh / 2} r={2} fill={FRAME} />);
  } else if (type.includes("fold")) {
    // Slide & Fold (bi-fold): N narrow leaves with fold mullions
    const leaves = w > 3600 ? 6 : w > 2400 ? 4 : 3;
    const pw = gw / leaves;
    for (let i = 0; i < leaves; i++) {
      els.push(panel(gx + pw * i, gy, pw, gh, `bf${i}`));
      if (i < leaves - 1) {
        const mx = gx + pw * (i + 1);
        els.push(<line key={`bfm${i}`} x1={mx} y1={gy} x2={mx} y2={gy + gh} stroke={FRAME} strokeWidth={i % 2 ? 2 : 1} />);
      }
    }
    els.push(slideArrow(gx + gw - pw, gy + gh - 12, pw * 0.6, "right", "bf-a"));
    els.push(<circle key="bf-h" cx={gx + gw - 5} cy={gy + gh / 2} r={2.2} fill={FRAME} />);
  } else if (type.includes("combo") || type.includes("openable")) {
    // Fixed + Openable: fixed picture (left) + side casement vent
    const fixW = gw * 0.62;
    els.push(panel(gx, gy, fixW, gh, "cmb-fx", GLASS));
    els.push(
      <text key="cmb-lbl" x={gx + fixW / 2} y={gy + gh / 2} textAnchor="middle" dominantBaseline="middle" fontSize="8" fill="#94a3b8" fontFamily="IBM Plex Mono, monospace">FIXED</text>
    );
    const ventX = gx + fixW;
    const ventW = gw - fixW;
    els.push(panel(ventX, gy, ventW, gh, "cmb-v"));
    els.push(swingSymbol(ventX, gy, ventW, gh, "right", "cmb-s"));
    els.push(<line key="cmb-m" x1={ventX} y1={gy} x2={ventX} y2={gy + gh} stroke={FRAME} strokeWidth={2} />);
  } else if (type.includes("door cum window")) {
    const winH = gh * 0.32;
    els.push(panel(gx, gy, gw, winH, "dcw-top", GLASS2));
    const doorY = gy + winH + 2;
    const doorH = gh - winH - 2;
    const half = gw / 2;
    els.push(panel(gx, doorY, half, doorH, "dcw-l"));
    els.push(panel(gx + half, doorY, gw - half, doorH, "dcw-r"));
    els.push(swingSymbol(gx, doorY, half, doorH, "right", "dcw-sl"));
    els.push(swingSymbol(gx + half, doorY, gw - half, doorH, "left", "dcw-sr"));
    // handles
    els.push(<circle key="dcw-hl" cx={gx + half - 5} cy={doorY + doorH / 2} r={2} fill={FRAME} />);
    els.push(<circle key="dcw-hr" cx={gx + half + 5} cy={doorY + doorH / 2} r={2} fill={FRAME} />);
  } else if (type.includes("lift")) {
    // Lift & Slide — 2 large panels
    const pw = gw / 2;
    els.push(panel(gx, gy, pw, gh, "ls0"));
    els.push(panel(gx + pw, gy, pw, gh, "ls1"));
    els.push(slideArrow(gx + pw * 1.5, gy + gh / 2, pw * 0.55, "left", "ls-a"));
    els.push(<line key="ls-m" x1={gx + pw} y1={gy} x2={gx + pw} y2={gy + gh} stroke={FRAME} strokeWidth={2} />);
    els.push(<circle key="ls-h" cx={gx + pw + 6} cy={gy + gh / 2} r={2.4} fill={FRAME} />);
  } else if (type.includes("sliding door")) {
    const panels = w > 2600 ? 3 : 2;
    const pw = gw / panels;
    for (let i = 0; i < panels; i++) {
      els.push(panel(gx + pw * i, gy, pw, gh, `sd${i}`));
      const dir = i === 0 ? "right" : i === panels - 1 ? "left" : "both";
      els.push(slideArrow(gx + pw * i + pw / 2, gy + gh - 12, pw * 0.5, dir, `sda${i}`));
    }
    els.push(<circle key="sd-h" cx={gx + pw - 5} cy={gy + gh / 2} r={2.2} fill={FRAME} />);
  } else if (type.includes("4 track") || type.includes("4t")) {
    const pw = gw / 4;
    for (let i = 0; i < 4; i++) {
      els.push(panel(gx + pw * i, gy, pw, gh, `t4${i}`));
      const dir = i === 0 ? "right" : i === 3 ? "left" : "both";
      els.push(slideArrow(gx + pw * i + pw / 2, gy + gh / 2, pw * 0.5, dir, `t4a${i}`));
    }
  } else if (type.includes("3 track") || type.includes("3t") || type.includes("3 leaf")) {
    const pw = gw / 3;
    for (let i = 0; i < 3; i++) {
      els.push(panel(gx + pw * i, gy, pw, gh, `t3${i}`));
      const dir = i === 0 ? "right" : i === 2 ? "left" : "both";
      els.push(slideArrow(gx + pw * i + pw / 2, gy + gh / 2, pw * 0.5, dir, `t3a${i}`));
    }
  } else if (type.includes("corner")) {
    const pw = gw / 2;
    els.push(panel(gx, gy, pw, gh, "cs0"));
    els.push(panel(gx + pw, gy, pw, gh, "cs1"));
    els.push(slideArrow(gx + pw * 0.5, gy + gh / 2, pw * 0.5, "left", "csa0"));
    els.push(slideArrow(gx + pw * 1.5, gy + gh / 2, pw * 0.5, "right", "csa1"));
    els.push(
      <line key="cs-corner" x1={gx + pw} y1={gy} x2={gx + pw} y2={gy + gh} stroke={FRAME} strokeWidth={2.5} />
    );
  } else if (type.includes("sliding") || type.includes("2 track") || type.includes("2t")) {
    const pw = gw / 2;
    els.push(panel(gx, gy, pw, gh, "s0"));
    els.push(panel(gx + pw, gy, pw, gh, "s1"));
    els.push(slideArrow(gx + pw * 0.5, gy + gh / 2, pw * 0.5, "right", "sa0"));
    els.push(slideArrow(gx + pw * 1.5, gy + gh / 2, pw * 0.5, "left", "sa1"));
  } else if (type.includes("top hung")) {
    els.push(panel(gx, gy, gw, gh, "th"));
    els.push(swingSymbol(gx, gy, gw, gh, "top", "th-s"));
  } else if (type.includes("casement")) {
    const two = w > 1200;
    if (two) {
      const pw = gw / 2;
      els.push(panel(gx, gy, pw, gh, "c0"));
      els.push(panel(gx + pw, gy, pw, gh, "c1"));
      els.push(swingSymbol(gx, gy, pw, gh, "left", "c0s"));
      els.push(swingSymbol(gx + pw, gy, pw, gh, "right", "c1s"));
    } else {
      els.push(panel(gx, gy, gw, gh, "c"));
      els.push(swingSymbol(gx, gy, gw, gh, "left", "cs"));
    }
  } else {
    // Fixed
    els.push(panel(gx, gy, gw, gh, "fx", GLASS));
    els.push(
      <text
        key="fixlbl"
        x={gx + gw / 2}
        y={gy + gh / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="9"
        fill="#64748b"
        fontFamily="IBM Plex Mono, monospace"
        letterSpacing="1"
      >
        FIXED
      </text>
    );
  }

  return (
    <svg
      viewBox={`0 0 ${svgW} ${svgH}`}
      preserveAspectRatio="xMidYMid meet"
      className={className}
      role="img"
      aria-label={`${typology} ${width} x ${height} mm`}
    >
      {/* outer frame */}
      <rect x={x0} y={y0} width={frameW} height={frameH} fill="#fff" stroke={FRAME} strokeWidth={3} />
      {els}

      {showDims && (
        <g fontFamily="IBM Plex Mono, monospace" fontSize="9" fill="#475569">
          {/* width dimension */}
          <line x1={x0} y1={12} x2={x1} y2={12} stroke="#94a3b8" strokeWidth={0.8} />
          <line x1={x0} y1={9} x2={x0} y2={15} stroke="#94a3b8" strokeWidth={0.8} />
          <line x1={x1} y1={9} x2={x1} y2={15} stroke="#94a3b8" strokeWidth={0.8} />
          <rect x={(x0 + x1) / 2 - 20} y={5} width={40} height={13} fill="#fff" />
          <text x={(x0 + x1) / 2} y={15} textAnchor="middle">
            {Math.round(w)}
          </text>
          {/* height dimension */}
          <line x1={x1 + 14} y1={y0} x2={x1 + 14} y2={y1} stroke="#94a3b8" strokeWidth={0.8} />
          <line x1={x1 + 11} y1={y0} x2={x1 + 17} y2={y0} stroke="#94a3b8" strokeWidth={0.8} />
          <line x1={x1 + 11} y1={y1} x2={x1 + 17} y2={y1} stroke="#94a3b8" strokeWidth={0.8} />
          <text
            x={x1 + 30}
            y={(y0 + y1) / 2}
            textAnchor="middle"
            transform={`rotate(90 ${x1 + 30} ${(y0 + y1) / 2})`}
          >
            {Math.round(h)}
          </text>
        </g>
      )}

      {(code || elevation) && (
        <text
          x={x0}
          y={svgH - 2}
          fontSize="8"
          fill="#94a3b8"
          fontFamily="IBM Plex Mono, monospace"
        >
          {[code, elevation].filter(Boolean).join(" · ")}
        </text>
      )}
    </svg>
  );
}
